/* eslint-disable no-underscore-dangle */
import * as LZString from 'lz-string';

export interface IWebApiRequestOptions {
  deferRequestsExecution?: boolean;
  skipCache?: boolean;
  autoPoll?: boolean;
  hash?: number;
}

export interface IWebApiRequestBody {
  [prop: string]: any;
  _config?: IWebApiRequestOptions;
}

export interface IWebApiRequest {
  [apiName: string]: IWebApiRequestBody;
}

export interface IWebApiRequestBodyInternal {
  [prop: string]: any;
  _config: IWebApiRequestOptionsInternal;
}

interface IWebApiRequestInternal {
  [apiName: string]: IWebApiRequestBodyInternal;
}

/// interface for the configuration parameter passed
/// to IWebApi.send()
interface IWebApiRequestOptionsInternal extends IWebApiRequestOptions {
  /** used to track hash on autopoll requests */
  _h?: any;
  _rh?: any;
  success(response: any, apiInfo: IWebApiResponseInfo): void;
  error?(response: any, ajaxResponse?: any): void;
  pendingRetries?: number;
  unauthorised(response: any): void;
}

interface IApiRequest {
  /** Service/API */
  S: string;
  /** API Request */
  R?: IApiRequestDetail;
  /** API Request hash - send back on subsequent polls as a "shorthand" for the original request. This prevents constant re-issuing of the original (unmodified) request */
  RH?: string;
  TId: number;
  Hash?: any;
  DeferExecution?: boolean;
  SkipCache?: boolean;
  /** Indicates the request failed, and has been re-attempted a second time */
  RetriedAfterFail?: boolean;
}

/// An individual API request
interface IApiRequestDetail {
  _config?: IWebApiRequestOptionsInternal;
}

export interface IWebApiResponseBundle {
  Success: boolean;
  Responses: IWebApiResponse[];
  PackageStatus: string;
  EvId: string;
  Date: string;
  PollFreqMin: number;
}

export enum WebApiModelState {
  Unknown = 'Unknown',
  New = 'New',
  Exists = 'Exists',
  ExistsUnchanged = 'ExistsUnchanged',
  ExistsChanged = 'ExistsChanged',
  Pending = 'Pending',
  ResendMiss = 'ResendMiss',
  InError = 'InError'
}

export enum WebApiRequestState {
  Unknown = 'Unknown',
  Completed = 'Completed',
  InvalidRequest = 'InvalidRequest',
  Unauthorized = 'Unauthorized',
  Unauthenticated = 'Unauthenticated',
  Failed = 'Failed'
}

export interface IWebApiResponseInfo {
  TId: string;
  Hash?: number;
  RequestHash?: string;
  PollFreq?: number;
  RequestStatus: WebApiRequestState;
  ModelStatus: WebApiModelState;
}

interface IWebApiResponse extends IWebApiResponseInfo {
  Model: any;
}

/// interface for the angular service returned by apiEndpoint
export interface IWebApi {
  send<T>(
    requests: IWebApiRequest,
    overridePending?: boolean,
    includeResponseInfo?: boolean,
    immediateDispatch?: boolean,
    enablePingSession?: boolean
  ): Promise<T & { _apiInfo?: IWebApiResponseInfo }>;
  poll<T>(
    request: IWebApiRequest,
    callback: (response?: T & { _apiInfo?: IWebApiResponseInfo }, error?: any) => void,
    overridePending?: boolean,
    includeResponseInfo?: boolean
  ): void;
}

export type WebApiHttpProviderHttpSend = (url: string, requestPayload: any, onSuccess: (response: any) => void, onError: (response: any) => void) => void;

// provides abstraction of the framework that implements the ajax request to our API.
// this allows the BaseWebApiEndpoint to support AJAX requests from both Jquery and Angular
interface IWebApiHttpProvider {
  webApiUrl: string;
  httpsend: WebApiHttpProviderHttpSend;
  unauthorised(reponse?: any): void;
  unauthenticated(reponse?: any): void;
}

// provides abstraction of the framework that implements timeouts.
// this allows angular clients to internally utilise $timeout to avoid losing $scope digests
export interface IWebApiTimeoutProvider {
  timeout(delayMs: number, fn: () => void): void;
}

export type WebApiConfig = {
  httpProvider: IWebApiHttpProvider;
  timeoutProvider: IWebApiTimeoutProvider;
  enableTrace?: boolean;
  pollingOff?: boolean;
  isDebug?: boolean;
  enableLzipApi?: boolean;
  enablePingSession?: boolean;
  apiCompressionThreshold?: number;
  scheduleRepollsMsDefault?: number;
  failRetryPollFreq?: number;
  scheduleSendMs?: number;
  maxPendingRequests?: number;
};

export const webApiBase = ({
  httpProvider,
  timeoutProvider,
  enableTrace = false,
  pollingOff = false,
  isDebug = false,
  enableLzipApi = true,
  apiCompressionThreshold = 2000,
  scheduleRepollsMsDefault = 1000,
  failRetryPollFreq = 1000,
  scheduleSendMs = 1000,
  maxPendingRequests = 10
}: WebApiConfig): IWebApi => {
  let webApiTrackingIdSeed: number = 0;
  let platform: string;
  const requestTrackingDictionary: { [id: string]: any } = {};
  const includeModelWrapperInResponse = false;
  let requestsInProgress = false;
  let pendingRequests: IApiRequest[] = [];
  let pendingRepollRequests: IApiRequest[] = [];

  let scheduleRepollsMs = scheduleRepollsMsDefault;

  const trace = (...params: any[]) => {
    const enableTraceOverrideOnApp = (process.env.REACT_APP_WEBTRADING_TRACE ?? '').toLowerCase() === 'enabled' && process.env.REACT_APP_STAGE !== 'production';
    if (enableTrace || enableTraceOverrideOnApp) {
      console.log(params); // eslint-disable-line no-console
    }
  };

  const queueRequests = (requestsObject: any, isRetryAfterFail: boolean, isRepoll: boolean, immediateDispatch: boolean, enablePingSession: boolean) => {
    // loop properties in javascript object, and use the NAME of the property as the target API handler.
    // The object within each property is the request for that handler.
    // eslint-disable-next-line guard-for-in
    for (const apiName in requestsObject) {
      webApiTrackingIdSeed += 1;
      const tid: number = webApiTrackingIdSeed; // generate unique TId for each API call

      // get request ....
      const apiRequestDetail: IApiRequestDetail = requestsObject[apiName];

      // ... and track each in a dictionary for the bundle to we can easily match them up when ajax returns
      requestTrackingDictionary[tid] = { Name: apiName, Request: { ...apiRequestDetail } };

      const apiRequestWrapper: IApiRequest = <IApiRequest>{
        S: apiName,
        R: apiRequestDetail,
        TId: tid,
        RetriedAfterFail: isRetryAfterFail
      };

      if (apiRequestDetail._config) {
        // allow polling to be disabled via URL for debug
        if (apiRequestDetail._config.autoPoll && pollingOff) {
          apiRequestDetail._config.autoPoll = false;
        }

        if (apiRequestDetail._config._h || apiRequestDetail._config.hash) {
          // get hash out of request config ...
          apiRequestWrapper.Hash = apiRequestDetail._config._h ?? apiRequestDetail._config.hash;
        }
        if (apiRequestDetail._config.skipCache) {
          apiRequestWrapper.SkipCache = true;
        }

        if (apiRequestDetail._config.deferRequestsExecution === false) {
          apiRequestWrapper.DeferExecution = false;
        }
      }

      if (immediateDispatch) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        attemptDispatch(true, apiRequestWrapper, enablePingSession);
        return;
      }

      // if queued for repoll timer, put in pending repolls and wait for next tick ...
      if (isRepoll) {
        pendingRepollRequests.push(apiRequestWrapper);
      } else {
        // otherwise we dispatch as soon as possible
        // Add the API call to the bundle
        pendingRequests.push(apiRequestWrapper);
      }
    }

    if (pendingRequests.length > maxPendingRequests) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      attemptDispatch(true, undefined, enablePingSession);
    }
  };

  const attemptDispatch = (overridePending?: boolean, immediateDispatchRequest?: IApiRequest, enablePingSession: boolean = false) => {
    // don't send if there's no work or stuff in progress or if we are only sending on a schedule
    if (!immediateDispatchRequest && (pendingRequests.length === 0 || (!overridePending && (requestsInProgress || scheduleSendMs > 0)))) return;

    // ok we're now in progress
    requestsInProgress = true;

    // create the JSON API bundle object, and at the same time REMOVE
    // all dispatching requests from the pending requests array.
    const requestBundle = {
      Rs: immediateDispatchRequest ? [immediateDispatchRequest] : pendingRequests.splice(0, pendingRequests.length) // now remove from queue
    };

    // If the ajax call fails, this will call error() in all requests
    const failAllRequests = (ajaxResponse: any) => {
      for (let i = 0; i < requestBundle.Rs.length; i += 1) {
        const request: IApiRequest = requestBundle.Rs[i];
        const originalRequest = requestTrackingDictionary[request.TId];
        const originalApiRequest: IWebApiRequestBodyInternal = originalRequest.Request;

        trace('failAllRequests', request, originalApiRequest);

        if (originalApiRequest?._config?.error) {
          if (request.RetriedAfterFail) {
            originalApiRequest?._config.error(undefined, 'Retry failed');
          } else {
            originalApiRequest?._config.error(undefined, ajaxResponse);
          }
        }
        trace(platform, 'Ajax call error, request RetriedAfterFail flag ...', request.RetriedAfterFail);
        if (!request.RetriedAfterFail && originalApiRequest?._config?.autoPoll === true) {
          const reqBundle: any = {};
          reqBundle[request.S] = request.R;

          trace(platform, 'REPOLLs ...', failRetryPollFreq, reqBundle);
          timeoutProvider.timeout(failRetryPollFreq, () => {
            trace(platform, 'REPOLL: dispatching failed ajax API　request ...', reqBundle);
            queueRequests(reqBundle, true, true, false, enablePingSession);
            attemptDispatch();
          });
        }
      }
    };

    let urlDebug = '';

    // show some diagnostic info on the URL in dev ....
    if (isDebug) {
      trace(platform, 'SEND (', requestBundle.Rs.length, ')', requestBundle.Rs);

      let qsDebug = '';
      for (let i = 0; i < requestBundle.Rs.length; i += 1) {
        qsDebug += `-${requestBundle.Rs[i].S}`;
      }

      const sendDate = new Date();
      urlDebug = `dt=${sendDate.getMinutes()}:${sendDate.getSeconds()}.${sendDate.getMilliseconds()}&apis=${qsDebug}`;
    }

    // clone api request to clean up unnecessary properties for dispatch
    const dispatchingRequest = { ...requestBundle };

    for (let i = 0; i < dispatchingRequest.Rs.length; i += 1) {
      const req = dispatchingRequest.Rs[i];

      // polling response hash and original request hash returned, can now omit
      // resend of the request until it changes
      if (req.R?._config?._h && req.R?._config?._rh) {
        req.RH = req.R._config._rh;
        delete req.R;
      } else {
        delete req.R?._config;
      }

      delete req.RetriedAfterFail;
    }

    let jsonReqStr = JSON.stringify(dispatchingRequest);
    let enableLz = false;

    if (enableLzipApi && jsonReqStr.length > apiCompressionThreshold) {
      jsonReqStr = LZString.compressToEncodedURIComponent(jsonReqStr);
      enableLz = true;
    }

    let url = httpProvider.webApiUrl;
    url += url.includes('?') ? '&' : '?';

    url += urlDebug;

    // set _cmc.pingsession:false, for web frontend, not update active session
    httpProvider.httpsend(
      url,
      { '_cmc.workload': jsonReqStr, '_cmc.pingsession': enablePingSession, '_cmc.lz': enableLz },
      (response: IWebApiResponseBundle) => {
        // ajax success

        if (response) trace(platform, 'RECEIVE:', urlDebug, response.Responses, response.PackageStatus, response.Success);

        if (response?.Success) {
          scheduleRepollsMs = response.PollFreqMin;

          const fullModel: any = {
            _raw: response
          };

          const repollQueue: any = {};

          // const debugStr = '=> ';

          const collectForRepoll = (apiRequest: any, pollFreq: number, apiName: string) => {
            if (!repollQueue[pollFreq]) {
              repollQueue[pollFreq] = [];
            }

            const reqBundle: any = {};
            reqBundle[apiName] = apiRequest;

            repollQueue[pollFreq].push(reqBundle);
          };

          const processApiResponse = (apiResponse: IWebApiResponse) => {
            const originalRequest = requestTrackingDictionary[apiResponse.TId];
            const apiName = originalRequest.Name;
            const originalApiRequest: IWebApiRequestBodyInternal = originalRequest.Request;

            // debugStr += `${apiName}:${apiResponse.RequestStatus}:${apiResponse.PollFreq}:${apiResponse.Hash}|`;

            // clone the api response info, but remove the model
            const apiResponseInfo = { ...apiResponse };
            delete apiResponseInfo.Model;

            if (apiResponse.RequestStatus === 'Failed') {
              if (originalApiRequest?._config?.error) {
                originalApiRequest._config.error(apiResponse);

                // setup auto-poll based on errored polling frequency
                if (originalApiRequest?._config?.autoPoll === true && apiResponse.PollFreq && apiResponse.PollFreq !== null) {
                  trace(platform, 'FAILED => REPOLL QUEUE', apiResponse.TId, apiResponse.ModelStatus, apiResponse.RequestStatus, apiResponse.PollFreq);
                  collectForRepoll(originalApiRequest, apiResponse.PollFreq, apiName);
                }
              }
            } else if (apiResponse.ModelStatus === 'InError' && apiResponse.RequestStatus === 'Unauthorized') {
              // request is processing, recall it

              trace(platform, 'UNAUTHORISED', originalRequest, apiResponse);

              httpProvider.unauthorised(apiResponse);

              if (originalApiRequest?._config?.unauthorised) {
                originalApiRequest._config.unauthorised(apiResponse);
              }
            } else if (apiResponse.ModelStatus === 'InError' && apiResponse.RequestStatus === 'Unauthenticated') {
              // request is processing, recall it
              // trace(platform, "Pending", apiResponse);
              trace(platform, 'UNAUTHENTICATED', originalRequest, apiResponse);
              httpProvider.unauthenticated(apiResponse);

              if (originalApiRequest._config && originalApiRequest._config.unauthorised) {
                originalApiRequest._config.unauthorised(apiResponse);
              }

              // API thread pending, or request hash resend has missed, in which case treat as pending
            } else if (apiResponse.ModelStatus === 'Pending' || apiResponse.ModelStatus === 'ResendMiss') {
              // request is processing, recall it
              trace(platform, 'Pending', apiResponse, originalApiRequest);

              // delete request hash so full request gets re-issued
              delete originalApiRequest._config._rh;

              originalApiRequest._config.pendingRetries = originalApiRequest._config.pendingRetries || 0;
              originalApiRequest._config.pendingRetries += 1;

              trace(platform, 'PENDING => REPOLL QUEUE', originalApiRequest._config.pendingRetries, originalRequest, apiResponse);

              if (originalApiRequest._config.pendingRetries < 60) {
                // OVERRIDE PollFreq for Pending - assumption is result will be returned soon,
                // whether it's successful or in error
                // repoll every second until pending returns result
                collectForRepoll(originalApiRequest, 1000 * originalApiRequest._config.pendingRetries, apiName);
              } else if (originalApiRequest._config && originalApiRequest._config.error) {
                originalApiRequest._config.error(apiResponse);
              }
            } else {
              // assign either the wrapper or model to the response
              // with the original API name.
              fullModel[apiName] = includeModelWrapperInResponse ? apiResponse : apiResponse.Model;

              // if a handler is declared in each individual request,
              // dispatch to it
              if (originalApiRequest?._config?.success) {
                trace(platform, 'SUCCESS => DISPATCH =>', apiName);

                // reset if successful
                originalApiRequest._config.pendingRetries = 0;

                // don't dispatch empty cache polling responses
                if (apiResponse.ModelStatus !== 'ExistsUnchanged') {
                  originalApiRequest._config.success(apiResponse.Model, apiResponseInfo);
                }

                trace({ 'originalApiRequest?._config?.autoPoll': originalApiRequest?._config?.autoPoll, 'apiResponse.PollFreq': apiResponse.PollFreq });
                // setup auto-poll on supporting API's
                if (originalApiRequest?._config?.autoPoll === true && apiResponse.PollFreq && apiResponse.PollFreq !== null) {
                  trace(
                    platform,
                    'SUCCESS => REPOLL QUEUE =>',
                    apiName,
                    'TID:',
                    apiResponse.TId,
                    '#',
                    apiResponse.Hash,
                    apiResponse.ModelStatus,
                    apiResponse.RequestStatus,
                    apiResponse.PollFreq,
                    'REQUEUING:',
                    originalApiRequest
                  );

                  // if a hash is returned, update original request
                  if (apiResponse.Hash) {
                    trace(platform, 'SETHASH', apiResponse.Hash, originalApiRequest._config.autoPoll);
                    // update hash in original request
                    originalApiRequest._config._h = apiResponse.Hash;
                    originalApiRequest._config._rh = apiResponse.RequestHash;
                  }

                  collectForRepoll(originalApiRequest, apiResponse.PollFreq, apiName);
                }
              } // if(_config.success)
            } // else (success)

            // clean up tracking dictionary (memory leak)
            delete requestTrackingDictionary[apiResponse.TId];
          };

          for (let i = 0; i < response.Responses.length; i += 1) {
            processApiResponse(response.Responses[i]);
          } // /for(responses)

          const queueRepoll = (pollFreq: number, queuedBundle: any) => {
            trace(platform, 'REPOLLs ...', pollFreq, queuedBundle);

            // no need to delay for 1s API's - repoll timer will apply necessary delay
            if (pollFreq <= 1000) {
              trace(platform, 'REPOLL: immediately dispatching delayed requests ...', queuedBundle.length, queuedBundle);
              for (const element of queuedBundle) {
                queueRequests(element, false, true, false, enablePingSession);
              }
              // attemptDispatch();
            } else {
              timeoutProvider.timeout(pollFreq, () => {
                trace(platform, 'REPOLL: dispatching delayed requests ...', queuedBundle);
                for (const element of queuedBundle) {
                  queueRequests(element, false, true, false, enablePingSession);
                }
                // attemptDispatch();
              });
            }
          };

          // now pick off all collected repolls and dispatch
          // repolls by their poll frequency
          // eslint-disable-next-line guard-for-in
          for (const pollFreq in repollQueue) {
            const queuedBundle = repollQueue[pollFreq];
            // call timeout in separate func otherwise we get polluted closure bug
            queueRepoll(Number(pollFreq), queuedBundle);
          }

          // trace(platform, debugStr);
        } else {
          trace(platform, 'ERROR =>', response, requestBundle);

          if (response.PackageStatus === 'Unauthorized') {
            httpProvider.unauthorised(response);
            failAllRequests(response);
          } else if (response.PackageStatus === 'Unauthenticated') {
            httpProvider.unauthenticated(response);
            failAllRequests(response);
          }

          // loop all origin requests and set as errored
          if (response && response.Responses) {
            for (let i = 0; i < requestBundle.Rs.length; i += 1) {
              const apiResponse: IWebApiResponse = response.Responses[i];

              const originalApiRequest = requestTrackingDictionary[apiResponse.TId].Request;

              if (originalApiRequest._config && originalApiRequest._config.error) {
                originalApiRequest._config.error(response);
              }
            }
          } else {
            // loop all origin requests set error
            failAllRequests(response);
          }
        }

        requestsInProgress = false;
        attemptDispatch();
      },
      // ajax error
      (response) => {
        // loop all origin requests set error
        failAllRequests(response);

        requestsInProgress = false;
        attemptDispatch();
      }
    );
  };
  // Repoll timer - this ticks constantly every second and dispatches any requests put in pendingRepollRequests
  const scheduleRepolls = () => {
    timeoutProvider.timeout(scheduleRepollsMs, () => {
      trace(platform, ' -========== scheduleRepolls ==========-');
      // wrap in TC to guarantee repoll
      try {
        if (pendingRepollRequests.length > 0) {
          // const pendingRepolls = pendingRepollRequests;
          const existing = pendingRequests.length;
          pendingRequests = [...pendingRequests, ...pendingRepollRequests];
          trace(platform, 'pendingRequests', existing, pendingRequests.length);
          pendingRepollRequests = [];
          attemptDispatch();
        }
      } catch {
        /* swallow */
      }
      scheduleRepolls();
    });
  };

  // start repoll timer
  scheduleRepolls();

  const scheduleSend = () => {
    timeoutProvider.timeout(scheduleSendMs, () => {
      trace(platform, ' -========== scheduleSend ==========-');
      // wrap in TC to guarantee repoll
      try {
        if (pendingRequests.length > 0) {
          // override to send
          attemptDispatch(true);
        }
      } catch {
        /* swallow */
      }
      scheduleSend();
    });
  };

  if (scheduleSendMs > 0) {
    scheduleSend();
  }

  /// sends a request to the CMC JSON API
  const send = (requests: any, overridePending?: boolean, immediateDispatch?: boolean, enablePingSession: boolean = false) => {
    // Loop either array or object properties directly ....
    if (Array.isArray(requests)) {
      // array means dev wants to send multiple API calls to same handler, so cannot use js property names. In this case "requests" will
      // be an array of single-request bundles
      for (const request of requests) {
        queueRequests(request, false, false, immediateDispatch!, enablePingSession);
      }
    } else {
      // here "requests" is a straight up object with handler names as js properties
      queueRequests(requests, false, false, immediateDispatch!, enablePingSession);
    }

    if (!immediateDispatch) {
      // Wait in case there are requests coming directly after this
      setTimeout(() => attemptDispatch(overridePending, undefined, enablePingSession), 0);
    }
  };

  const sendRequests = <T>(
    request: IWebApiRequest,
    overridePending?: boolean,
    includeResponseInfo: boolean = true,
    immediateDispatch: boolean = false,
    enablePingSession: boolean = false
  ) => {
    const promise = new Promise<T & { _apiInfo?: IWebApiResponseInfo }>((resolve, reject) => {
      const keys = Object.keys(request);
      if (keys.length !== 1) {
        throw new Error('Only one request is supported');
      }
      const _config: IWebApiRequestOptionsInternal = {
        ...request[keys[0]]._config,
        success: (response: T & { _apiInfo?: IWebApiResponseInfo }, apiInfo: IWebApiResponseInfo) => {
          trace('success', response, apiInfo);
          if (includeResponseInfo) response._apiInfo = apiInfo;
          resolve(response);
        },
        error: (response: any, reason: any) => {
          trace('error', response, reason);
          reject(response || reason);
        },
        unauthorised: (response) => {
          trace('unauthorised', response);
          reject(response);
        }
      };
      const body: IWebApiRequestBodyInternal = {
        ...request[keys[0]],
        _config
      };
      const req: IWebApiRequestInternal = {};
      req[keys[0]] = body;
      send(req, overridePending, immediateDispatch, enablePingSession);
    });
    return promise;
  };

  const pollRequests = <T>(
    request: IWebApiRequest,
    callback: (response?: T & { _apiInfo?: IWebApiResponseInfo }, error?: any) => void,
    overridePending?: boolean,
    includeResponseInfo: boolean = true
  ): void => {
    const keys = Object.keys(request);
    if (keys.length !== 1) {
      throw new Error('Only one request is supported');
    }
    const _config: IWebApiRequestOptionsInternal = {
      ...request[keys[0]]._config,
      success: (response: T & { _apiInfo?: IWebApiResponseInfo }, apiInfo: IWebApiResponseInfo) => {
        trace('success', response);
        if (includeResponseInfo) response._apiInfo = apiInfo;
        callback(response);
      },
      error: (response: any, reason: any) => {
        trace('error', response, reason);
        callback(undefined, response || reason);
      },
      unauthorised: (response) => {
        trace('unauthorised', response);
        callback(undefined, response);
      }
    };
    const body: IWebApiRequestBodyInternal = {
      ...request[keys[0]],
      _config
    };
    body._config.autoPoll = true;
    const req: IWebApiRequestInternal = {};
    req[keys[0]] = body;
    send(req, overridePending);
  };

  return { send: sendRequests, poll: pollRequests };
};
